// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useState } from "react";
import { CheckCircle2, Plus } from "lucide-react";

import type { Dialect, Family, ModelType, Repository } from "../../../shared/contracts";

import ModelTypeIcon from "../../components/ModelTypeIcon";
import { Button, Field, Modal, Notice, dialectLabel, errorText, modelTypeLabel } from "../../components/Primitives";

import { dialects, modelTypes } from "../../foundation/modelOptions.js";

export function NewModelDialog({
  repositories,
  initialRepository,
  onClose,
  onCreate,
  onCreateRepository,
}: {
  repositories: Repository[];
  initialRepository?: string;
  onClose: () => void;
  onCreateRepository?: () => void;
  onCreate: (data: any) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState<ModelType>("logical");
  const [family, setFamily] = useState<Family>("relational");
  const [dialect, setDialect] = useState<Dialect>("postgresql");
  const [repo, setRepo] = useState(
    initialRepository || repositories[0]?.repositoryId || "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onCreate({
        name: name.trim(),
        modelType: type,
        family,
        ...(family === "relational" && ["physical", "logical-physical"].includes(type) ? {targetProfile: dialect} : {}),
        repositoryId: repo,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Create a model"
      subtitle="Start with an empty canvas. Add only what your design needs."
      onClose={onClose}
      wide
      footer={
        <>
          <span className="footer-note">No sample entities are added.</span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={Plus}
            variant="primary"
            busy={busy}
            disabled={!name.trim() || !repo}
            onClick={submit}
          >
            Create model
          </Button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {!repositories.length && (
        <Notice kind="warning">
          A model needs a repository.{" "}
          {onCreateRepository ? (
            <>
              <br />
              <Button onClick={onCreateRepository}>
                Create your first repository
              </Button>
            </>
          ) : (
            "Ask your administrator to assign a repository."
          )}
        </Notice>
      )}
      <div className="form-row">
        <Field label="Model name">
          <input
            autoFocus
            placeholder="e.g. Customer operations"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Repository">
          <select value={repo} onChange={(e) => setRepo(e.target.value)}>
            {repositories.map((r) => (
              <option key={r.repositoryId} value={r.repositoryId}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Model family">
        <select
          value={family}
          onChange={(e) => setFamily(e.target.value as Family)}
        >
          <option value="relational">
            Relational · entities and relationships
          </option>
          <option value="semantic">
            Semantic · business definitions and grain
          </option>
          <option value="analytical">Analytical · facts and dimensions</option>
          <option value="document">
            Document · collections and nested fields
          </option>
          <option disabled>Graph · not available in this build</option>
          <option disabled>Process · not available in this build</option>
        </select>
      </Field>
      {family === "relational" ? (
        <>
          <div className="section-label">Where does your design begin?</div>
          <div className="model-type-grid">
            {modelTypes.map((t, i) => {
              
              return (
                <button
                  key={t}
                  className={`model-type-choice ${type === t ? "selected" : ""}`}
                  onClick={() => setType(t)}
                >
                  <ModelTypeIcon type={t} size={66} />
                  <strong>{modelTypeLabel(t)}</strong>
                  <span>
                    {
                      [
                        "Business concepts and how they relate.",
                        "Detailed attributes, rules, and business keys.",
                        "Logical meaning and target-ready structure.",
                        "Tables, columns, and database constraints.",
                      ][i]
                    }
                  </span>
                  {type === t && (
                    <CheckCircle2 size={17} className="choice-check" />
                  )}
                </button>
              );
            })}
          </div>
          {["physical", "logical-physical"].includes(type) && <Field
            label="Database target"
            hint="Used for physical design and SQL generation. Creating a model does not connect to a database."
          >
            <select
              value={dialect}
              onChange={(e) => setDialect(e.target.value as Dialect)}
            >
              {dialects.map((d) => (
                <option key={d} value={d}>
                  {dialectLabel(d)}
                </option>
              ))}
            </select>
          </Field>}
        </>
      ) : (
        <Notice>
          This opens a {family} design. Execution and runtime support depend on
          the declared adapter profile.
        </Notice>
      )}
    </Modal>
  );
}
